<template>
  <div v-show="showCarousel" class="content-slider critical-component-clear">
    <div class="container">
      <div v-if="liquid.sectionTitle" class="content-slider__header">
        <div class="content-slider__header-text">
          <h2
            v-if="liquid.sectionTitle"
            class="content-slider__title text-mobile-headlines-h3 text-desktop-headline-h3"
            v-text="liquid.sectionTitle"
          />
        </div>
        <btn
          v-if="liquid.overviewLink !== '' && liquid.overviewLabel !== ''"
          class="text-mobile-paragraph-m-regular-link text-desktop-paragraph-m-regular-link"
          modifiers="text"
          size="small-small"
          :url="liquid.overviewLink"
          :label="liquid.overviewLabel"
        />
      </div>
      <div
        class="content-slider__splide splide"
        :js-section-id="liquid.sectionId"
      >
        <div class="splide__track carousel">
          <div class="content-slider__list carousel splide__list">
            <div
              v-for="(item, index) of items"
              :key="index"
              class="content-slider__content splide__slide"
            >
              <content-card
                :badge="item.badge"
                :title="item.title"
                :description="item.description"
                :level="item.level"
                :goals="item.goals"
                :cta-label="liquid.ctaLabel"
                :cta-url="item.onlineStoreUrl"
                :teaser-image="item.teaser_image"
                :display-features="item.displayFeatures"
              />
            </div>
          </div>
        </div>
        <div class="splide__arrows">
          <button class="splide__arrow splide__arrow--prev" type="button">
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-arrow-left />
          </button>

          <button class="splide__arrow splide__arrow--next" type="button">
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-arrow-right />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: content-slider (content-slider)
 * -----------------------------------------------------------------------------
 * Content Slider for plans.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.sectionTitle - Section setting title.
 * @param {String} liquid.ctaLabel - CTA label.
 * @param {String} liquid.overviewLabel - Back to overview label.
 * @param {String} liquid.overviewLink - Back to overview link.
 * @param {String} liquid.itemsHandle - Handle of the metaobject to load
 *
 */
import { Splide } from '@splidejs/splide';
import { mapState } from 'pinia';
import useIndexStore from '~stores/index';

import Btn from '~global/btn/btn';
import ContentCard from '~async/content-card/content-card';
import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';
import sliderQuery from '~graphql/content-slider.gql';
import { transformFieldsToObject } from '~/format/format-content-metaobject';

export default {
  name: 'ContentSlider',

  components: {
    Btn,
    IconArrowLeft,
    IconArrowRight,
    ContentCard,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        sectionTitle: '',
        ctaLabel: '',
        overviewLabel: '',
        overviewLink: '',
        itemsHandle: '',
        limit: 12,
      }),
    },
  },

  data() {
    return {
      carousel: null,
      carouselRef: null,
      items: Array.from({ length: this.liquid.placeholderCount }, () => false),
      state: 'loading',
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),

    /**
     * Compute query variables.
     * @returns {Object}
     */
    queryVariables() {
      const { itemsHandle, limit } = this.liquid;
      return {
        country: this.$variable('store.country'),
        handle: {
          handle: itemsHandle.toLowerCase().replace(/ /g, '-'),
          type: 'plans_recommendation_list',
        },
        language: this.$variable('store.language'),
        limit,
      };
    },

    /**
     * Compute whether to show carousel.
     * - Hide 'last-seen' carousel by default.
     * @returns {Boolean}
     */
    showCarousel() {
      if (this.state === 'loading') {
        return false;
      }

      return this.items.length > 0;
    },

    /**
     * Search and filter items.
     * - Filtered items
     * @returns {Object}
     */
    filteredItems() {
      if (this.state !== 'ready' || !this.items) {
        return [];
      }

      return this.items?.filter((item) => {
        // Filter based on search query

        const lowerCaseQuery = this.searchQuery.toLowerCase();

        if (
          !item.title.toLowerCase().includes(lowerCaseQuery) &&
          !item.description.toLowerCase().includes(lowerCaseQuery)
        ) {
          return false;
        }

        let filterApplies = true;
        let noFilterSelected = true;

        // Filter based on selected filters
        for (const [key, values] of Object.entries(this.filters)) {
          noFilterSelected = values.length === 0 && noFilterSelected;

          filterApplies =
            (item.filterFeatures.filter((feature) => {
              return feature.key === key && values.includes(feature.value);
            }).length > 0 ||
              values.length === 0) &&
            filterApplies;
        }

        return filterApplies || noFilterSelected;
      });
    },

    /**
     * Paginated items.
     * @returns {Array}
     */
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.state === 'ready' && this.filteredItems.slice(start, end);
    },

    /**
     * Compute total pages.
     * set to 1 if no items are found
     * @returns {Number}
     */
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredItems.length / this.itemsPerPage),
      );
    },

    /**
     * Compute label text with currently shown plans.
     * @returns {String}
     */
    totalItemsLabel() {
      if (this.itemsCount === 1) {
        return `(${this.$string('plans.count_single', {
          replace: {
            count: this.itemsCount,
          },
        })})`;
      }

      return `(${this.$string('plans.count_plural', {
        replace: {
          count: this.itemsCount,
        },
      })})`;
    },
  },

  watch: {
    /**
     * Watch is mobile update to handle resize.
     */
    async isMobile() {
      await this.$nextTick();
      this.handleResize();
    },
  },

  mounted() {
    this.carouselRef = `#vue-element [js-section-id="${this.liquid.sectionId}"]`;
    this.fetchMetaobjects();
  },

  methods: {
    /**
     * Transform plan features.
     * @param {Array} items - Plan items.
     * @returns {Array}
     */
    transformPlanFeatures(items) {
      return items.map((item) => {
        const displayFeatures = item.feature_items
          ?.filter((featureItem) => featureItem.use_in_card_and_header)
          .map((featureItem) => {
            return {
              description: featureItem.description,
              icon: featureItem.icon?.image.url ?? '',
            };
          });

        const filterFeatures = item.feature_items
          ?.filter((featureItem) => featureItem.use_in_filter)
          .map((featureItem) => {
            return {
              key: featureItem.criteria,
              value: featureItem.description,
            };
          });
        return {
          ...item,
          displayFeatures: displayFeatures ?? [],
          filterFeatures: filterFeatures ?? [],
        };
      });
    },

    /**
     * Fetch metaobjects based on hanlde.
     */
    async fetchMetaobjects() {
      const response = await cnvs.Query({
        query: sliderQuery,
        variables: {
          ...this.queryVariables,
        },
      });

      if (!response.metaobject) {
        return;
      }

      this.buildSliderItems(response.metaobject);
      if (!this.isMobile) {
        this.constructCarousel();
      }
      this.state = 'ready';
    },

    buildSliderItems(metaobject) {
      const itemsList = transformFieldsToObject(metaobject);

      this.items = itemsList.trainingplans
        ? this.transformPlanFeatures(itemsList.trainingplans)
        : [];
      // try for nutrition plans
      if (this.items.length === 0) {
        this.items = itemsList.nutritionplans
          ? this.transformPlanFeatures(itemsList.nutritionplans)
          : [];
      }
    },

    /**
     * Construct the carousel.
     */
    async constructCarousel() {
      if (!this.carouselRef) {
        return;
      }

      await this.$nextTick();

      this.carousel = new Splide(this.carouselRef, {
        arrows: !this.isMobile,
        drag: false,
        focus: false,
        gap: 'var(--layout-gutter)',
        keyboard: true,
        paginationKeyboard: true,
        mediaQuery: 'min',
        omitEnd: true,
        pagination: false,
        perMove: 1,
        perPage: 3,
        slideFocus: false,
        snap: true,
        speed: this.$timing('normal'),
        type: 'slide',
      });

      this.carousel.mount();
    },

    /**
     * Handle resize event.
     */
    handleResize() {
      if (!this.isMobile) {
        this.constructCarousel();
        return;
      }

      if (this.isMobile && this.carousel) {
        this.carousel.destroy();
      }
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './content-slider';
</style>
